"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
export default function MobileMiningSystems() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const specs = [{
    label: "Power Capacity",
    value: "2.5 MW per unit"
  }, {
    label: "Cooling System",
    value: "Immersion-cooled ASIC arrays"
  }, {
    label: "Energy Source",
    value: "Stranded methane conversion"
  }, {
    label: "Deployment Time",
    value: "72 hours on-site"
  }, {
    label: "Carbon Reduction",
    value: "Up to 85% vs. grid baseline"
  }];
  return <section id="mobile-mining-systems" className="py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={titleRef} className={`mb-12 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="font-display font-semibold text-[clamp(2rem,4.5vw,2.75rem)] leading-[1.3] tracking-tight">
            Mobile Mining Systems
          </h2>
          <p className="mt-4 text-muted-foreground leading-[1.6] max-w-2xl">
            Engineered for rapid deployment and optimized energy efficiency in
            remote locations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className="space-y-1">
            {specs.map((spec, i) => <SpecItem key={i} spec={spec} index={i} />)}
          </div>

          {}
          <div className="relative">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                <CarouselItem data-index={0}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-sm)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.15)]">
                    <img src="https://calm-primate.10web.cloud/wp-content/uploads/2026/02/asic.png" alt="Mobile mining unit exterior view" className="w-full h-full object-cover" />
                  </div>
                </CarouselItem>
                <CarouselItem data-index={1}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-sm)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.15)]">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/immersion_cooling_system_detail.png" alt="Immersion cooling system detail" className="w-full h-full object-cover" />
                  </div>
                </CarouselItem>
                <CarouselItem data-index={2}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-sm)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.15)]">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/mobile_unit_deployment_site.png" alt="Mobile unit at deployment site" className="w-full h-full object-cover" />
                  </div>
                </CarouselItem>
                <CarouselItem data-index={3}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-sm)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.15)]">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/mining_rig_control_panel.png" alt="Mining rig control panel interface" className="w-full h-full object-cover" />
                  </div>
                </CarouselItem>
                <CarouselItem data-index={4}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-sm)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.15)]">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545031/img/methane_conversion_equipment.png" alt="Methane conversion equipment" className="w-full h-full object-cover" />
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="opacity-0 hover:opacity-100 transition-opacity duration-[var(--duration-medium)] -left-4 lg:-left-12" />
              <CarouselNext className="opacity-0 hover:opacity-100 transition-opacity duration-[var(--duration-medium)] -right-4 lg:-right-12" />
            </Carousel>
          </div>
        </div>
      </div>
    </section>;
}
function SpecItem({
  spec,
  index
}: {
  spec: {
    label: string;
    value: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <div ref={ref} className={`py-4 border-b border-border transition-all ease-[var(--ease-smooth)]`} style={{
    transitionDuration: "var(--duration-medium)",
    transitionDelay: isVisible ? `${index * 50}ms` : "0ms",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(10px)"
  }}>
      <div className="grid grid-cols-[1fr,auto] gap-4 items-baseline">
        <span className="font-sans text-foreground font-medium">{spec.label}</span>
        <span className="font-sans text-muted-foreground text-right">{spec.value}</span>
      </div>
    </div>;
}